import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback } from "react";
const SlideOperationsContext = createContext(undefined);
export const SlideOperationsProvider = ({ children }) => {
    const [slideOperations, setSlideOperations] = useState([]);
    const [sectionOperations, setSectionOperations] = useState([]);
    const [hasUnreportedOperations, setHasUnreportedOperations] = useState(false);
    const addSlideOperation = useCallback((operation) => {
        setSlideOperations(prev => {
            // For reordered slides, check if we already have a reorder operation for this slide
            if (operation.operation === 'reordered') {
                const existingIndex = prev.findIndex(op => op.slideId === operation.slideId && op.operation === 'reordered');
                if (existingIndex !== -1) {
                    // Update existing reorder operation with new position
                    const updated = [...prev];
                    updated[existingIndex] = {
                        ...updated[existingIndex],
                        newPosition: operation.newPosition,
                        timestamp: operation.timestamp,
                    };
                    console.log('[user_reminders] Updated existing reorder operation:', {
                        slideId: operation.slideId,
                        slideName: operation.slideName,
                        source: operation.source,
                        previousPosition: updated[existingIndex].previousPosition,
                        newPosition: operation.newPosition,
                    });
                    return updated;
                }
            }
            else {
                // For non-reordered operations, check if we already have this exact operation
                // (same slideId and operation type)
                const existingIndex = prev.findIndex(op => op.slideId === operation.slideId && op.operation === operation.operation);
                if (existingIndex !== -1) {
                    // Operation already exists, don't add duplicate
                    console.log('[user_reminders] Duplicate slide operation prevented:', operation);
                    return prev;
                }
            }
            console.log('[user_reminders] ➕ Added slide operation to reminder list:', {
                operation: operation.operation,
                slideId: operation.slideId,
                slideName: operation.slideName,
                sectionName: operation.sectionName,
                slideNumber: operation.slideNumber,
                source: operation.source || 'unknown',
                willShowInChangeTracker: operation.source !== 'ui',
            });
            return [...prev, operation];
        });
        setHasUnreportedOperations(true);
    }, []);
    const addSectionOperation = useCallback((operation) => {
        setSectionOperations(prev => {
            // For reordered sections, check if we already have a reorder operation for this section
            if (operation.operation === 'reordered') {
                const existingIndex = prev.findIndex(op => op.sectionId === operation.sectionId && op.operation === 'reordered');
                if (existingIndex !== -1) {
                    // Update existing reorder operation with new position
                    const updated = [...prev];
                    updated[existingIndex] = {
                        ...updated[existingIndex],
                        newPosition: operation.newPosition,
                        timestamp: operation.timestamp,
                    };
                    console.log('[user_reminders] Updated existing section reorder operation:', {
                        sectionId: operation.sectionId,
                        sectionName: operation.sectionName,
                        source: operation.source,
                        previousPosition: updated[existingIndex].previousPosition,
                        newPosition: operation.newPosition,
                    });
                    return updated;
                }
            }
            // For added/deleted, check if section already exists and remove it first (can't be both added and deleted)
            const existingIndex = prev.findIndex(op => op.sectionId === operation.sectionId);
            if (existingIndex !== -1) {
                // Remove existing operation for this section
                const updated = prev.filter((_, idx) => idx !== existingIndex);
                console.log('[user_reminders] ➕ Replaced section operation in reminder list:', {
                    operation: operation.operation,
                    sectionId: operation.sectionId,
                    sectionName: operation.sectionName,
                    source: operation.source || 'unknown',
                    willShowInChangeTracker: operation.source !== 'ui',
                });
                return [...updated, operation];
            }
            console.log('[user_reminders] ➕ Added section operation to reminder list:', {
                operation: operation.operation,
                sectionId: operation.sectionId,
                sectionName: operation.sectionName,
                source: operation.source || 'unknown',
                willShowInChangeTracker: operation.source !== 'ui',
            });
            return [...prev, operation];
        });
        setHasUnreportedOperations(true);
    }, []);
    const clearSlideOperations = useCallback(() => {
        setSlideOperations([]);
        setHasUnreportedOperations(false);
        console.log('[SlideOperationsContext] Cleared slide operations');
    }, []);
    const clearSectionOperations = useCallback(() => {
        setSectionOperations([]);
        setHasUnreportedOperations(false);
        console.log('[SlideOperationsContext] Cleared section operations');
    }, []);
    const clearAllOperations = useCallback(() => {
        setSlideOperations([]);
        setSectionOperations([]);
        setHasUnreportedOperations(false);
        console.log('[SlideOperationsContext] Cleared all operations');
    }, []);
    const value = {
        slideOperations,
        sectionOperations,
        addSlideOperation,
        addSectionOperation,
        clearSlideOperations,
        clearSectionOperations,
        clearAllOperations,
        hasUnreportedOperations: hasUnreportedOperations || slideOperations.length > 0 || sectionOperations.length > 0,
    };
    return (_jsx(SlideOperationsContext.Provider, { value: value, children: children }));
};
export const useSlideOperations = () => {
    const context = useContext(SlideOperationsContext);
    if (!context) {
        throw new Error("useSlideOperations must be used within a SlideOperationsProvider");
    }
    return context;
};
// Backwards compatibility exports
export const InsertedSlidesProvider = SlideOperationsProvider;
export const useInsertedSlides = useSlideOperations;
